import axios from 'axios';
import type { ApiErrorBody } from '@/types';

/**
 * A single error type for the whole UI. Every network/validation failure is
 * normalised into this so components never branch on `axios.isAxiosError`.
 */
export class AppError extends Error {
  readonly status: number;
  /** Field-level messages, keyed by form field, for react-hook-form. */
  readonly fieldErrors: Record<string, string[]>;
  readonly code?: string;
  readonly cause?: unknown;

  constructor(
    message: string,
    status = 0,
    fieldErrors: Record<string, string[]> = {},
    cause?: unknown,
    code?: string,
  ) {
    super(message);
    this.name = 'AppError';
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.cause = cause;
    this.code = code;
  }

  /** 401/403 – the user must re-authenticate or lacks permission. */
  get isAuthError(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** 4xx other than auth – retrying will not help. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

const FALLBACK_MESSAGES: Record<number, string> = {
  0: 'Impossibile contattare il server. Verifica la connessione e riprova.',
  400: 'Richiesta non valida.',
  401: 'Sessione scaduta. Effettua di nuovo l’accesso.',
  403: 'Non hai i permessi necessari per questa operazione.',
  404: 'Risorsa non trovata.',
  409: 'Operazione in conflitto con lo stato attuale dei dati.',
  422: 'Alcuni campi non sono validi.',
  429: 'Troppe richieste. Attendi qualche istante.',
  500: 'Errore interno del server. Riprova più tardi.',
  502: 'Il servizio non è raggiungibile. Riprova tra qualche secondo.',
  503: 'Servizio temporaneamente non disponibile.',
  504: 'Il server ha impiegato troppo tempo a rispondere. Riprova.',
};

function firstMessage(body: ApiErrorBody | string | undefined, status: number): string {
  if (typeof body === 'string') {
    if (/<html/i.test(body) || /bad gateway/i.test(body)) {
      return FALLBACK_MESSAGES[status] ?? 'Il servizio non è raggiungibile. Riprova tra qualche secondo.';
    }
    if (import.meta.env.DEV && status >= 500) {
      return 'Backend non raggiungibile su localhost:3000. Avvia NestJS, oppure lascia attivo il mock (VITE_USE_MOCK_API=true).';
    }
    return body || FALLBACK_MESSAGES[status] || 'Si è verificato un errore inatteso.';
  }
  if (!body || typeof body !== 'object') {
    if (import.meta.env.DEV && status >= 500) {
      return 'Backend non raggiungibile su localhost:3000. Avvia NestJS, oppure lascia attivo il mock (VITE_USE_MOCK_API=true).';
    }
    return FALLBACK_MESSAGES[status] ?? 'Si è verificato un errore inatteso.';
  }
  const { message } = body;
  if (Array.isArray(message)) return message[0] ?? FALLBACK_MESSAGES[status] ?? 'Errore';
  if (typeof message === 'string' && message.length > 0) return message;
  return FALLBACK_MESSAGES[status] ?? 'Si è verificato un errore inatteso.';
}

/**
 * NestJS `ValidationPipe` returns `message: string[]` shaped as
 * `"field must be ..."`. We keep the flat array but also try to bucket entries
 * by field so forms can highlight the offending inputs.
 */
function extractFieldErrors(body: ApiErrorBody | string | undefined): Record<string, string[]> {
  if (!body || typeof body !== 'object') return {};
  if (body.details) return body.details;
  if (!Array.isArray(body.message)) return {};

  return body.message.reduce<Record<string, string[]>>((acc, entry) => {
    const field = entry.split(' ')[0];
    if (!field) return acc;
    const bucket = acc[field] ?? [];
    bucket.push(entry);
    acc[field] = bucket;
    return acc;
  }, {});
}

/** Converts anything thrown by axios (or elsewhere) into an `AppError`. */
export function toAppError(error: unknown): AppError {
  if (error instanceof AppError) return error;

  if (axios.isAxiosError<ApiErrorBody | string>(error)) {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    return new AppError(firstMessage(body, status), status, extractFieldErrors(body), error, body && typeof body === 'object' ? body.code : undefined);
  }

  if (error instanceof Error) return new AppError(error.message, 0, {}, error);
  return new AppError('Si è verificato un errore inatteso.', 0, {}, error);
}

/** Safe message extraction for toasts and error states. */
export function getErrorMessage(error: unknown): string {
  return toAppError(error).message;
}
